import React, { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, type ApiApplication } from "../api.ts";
import { Button } from "../components/button.tsx";
import { ErrorBanner } from "../components/error-banner.tsx";
import { FieldGroup } from "../components/field-group.tsx";
import { Input } from "../components/input.tsx";

export function Dashboard() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<ApiApplication[] | null>(null);
  const [newAppName, setNewAppName] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    api
      .listApplications()
      .then((response) => setApplications(response.applications))
      .catch((error: Error) => setErrorMessage(error.message));
  }, []);

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedName = newAppName.trim();
    if (!trimmedName) {
      return;
    }

    setIsCreating(true);
    setErrorMessage(null);
    try {
      const { application, clientSecret } = await api.createApplication(trimmedName);
      navigate(`/applications/${application.id}`, { state: { clientSecret } });
    } catch (error) {
      setErrorMessage((error as Error).message);
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <>
      <h1 className="font-mono text-2xl font-bold uppercase tracking-[0.1em]">
        Applications
      </h1>

      <ErrorBanner message={errorMessage} className="mt-6" />

      <form onSubmit={handleCreate} className="mt-6">
        <FieldGroup>
          <Input
            value={newAppName}
            onChange={(event) => setNewAppName(event.target.value)}
            placeholder="New application name"
            required
          />
          <Button type="submit" disabled={isCreating}>
            Create
          </Button>
        </FieldGroup>
      </form>

      {applications === null ? (
        <p className="mt-8 font-mono text-xs uppercase tracking-wider text-muted">
          Loading…
        </p>
      ) : applications.length === 0 ? (
        <p className="mt-8 font-mono text-xs uppercase tracking-wider text-muted">
          No applications yet.
        </p>
      ) : (
        <ul className="mt-8 space-y-4">
          {applications.map((application) => (
            <li key={application.id}>
              <Link
                to={`/applications/${application.id}`}
                className="flex items-center justify-between gap-4 border-2 border-ink px-5 py-4 no-underline hover:bg-panel"
              >
                <div className="min-w-0">
                  <div className="font-semibold">{application.name}</div>
                  <div className="mt-1 truncate font-mono text-xs text-muted">
                    {application.clientId}
                  </div>
                </div>
                <span className="shrink-0 font-mono text-xs uppercase tracking-wider text-muted">
                  {new Date(application.createdAt).toLocaleDateString()}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
